import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from '@workspace/ui/components/dialog';
import { Button } from '@workspace/ui/components/button';
import { Input } from '@workspace/ui/components/input';
import { Textarea } from '@workspace/ui/components/textarea';
import { cn } from '@/utils';
import { useEnterApi } from '@enter/api';
import { useToast } from '@/contexts/toast';
import { useMixpanel } from '@/contexts/mixpanel';
import { useTranslations } from 'next-intl';

interface FormData {
    name: string;
    email: string;
    message: string;
}

interface BookingModalProps {
    children?: React.ReactNode;
    /** 受控模式：外部控制弹窗打开状态 */
    open?: boolean;
    /** 受控模式：弹窗状态变化回调 */
    onOpenChange?: (open: boolean) => void;
    /** Enables Landing V2's unified click contract for modal buttons. */
    trackingSurface?: 'landing_v2';
}

export const BookingModal = ({
    children,
    open: controlledOpen,
    onOpenChange,
    trackingSurface,
}: BookingModalProps) => {
    const [internalOpen, setInternalOpen] = useState(false);
    
    // 支持受控和非受控模式
    const isControlled = controlledOpen !== undefined;
    const open = controlledOpen ?? internalOpen;
    const setOpen = (value: boolean) => {
        if (!isControlled) {
            setInternalOpen(value);
        }
        onOpenChange?.(value);
    };
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        message: ''
    });
    const [emailError, setEmailError] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const api = useEnterApi();
    const { showSuccess, showError } = useToast();
    const { logger } = useMixpanel();
    const t = useTranslations('contactUs.modal');

    const isEmailValid = formData.email.trim()
        ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
        : false;

    const isFormValid = !!(formData.name.trim() && formData.email.trim() && formData.message.trim() && isEmailValid);

    const handleSubmit = async () => {
        if (!isFormValid || isSubmitting) {
            if (!isEmailValid) {
                setEmailError(t('invalidEmail'));
            }
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await api.onboarding.createConsultation(formData);

            if (response?.code !== 0) {
                throw new Error(response?.message || 'Failed to send message');
            }

            logger('home_contactus_submit', {
                topic: 'contact_us',
                role: 'unknown',
                workspace_size: 'unknown',
            });

            showSuccess(t('successToast'));
            setOpen(false);
            setFormData({ name: '', email: '', message: '' });
            setEmailError('');
        } catch (error: unknown) {
            console.error('Failed to submit consultation:', error);
            const message = error instanceof Error && error.message ? error.message : t('errorToast');
            showError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setOpen(false);
        setFormData({ name: '', email: '', message: '' });
        setEmailError('');
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {children ? (
                <DialogTrigger asChild>
                    {children}
                </DialogTrigger>
            ) : null}
            <DialogContent
                className="contact-dialog flex w-[640px]! max-w-[90vw]! flex-col items-start gap-8 rounded-[24px] border border-[#e4e4e7] bg-background p-10! shadow-[0_24px_48px_-12px_rgb(0_0_0/0.18)] z-[2001]!"
                overlayClassName="z-[2000]!"
                showCloseButton={false}
            >
                <DialogHeader className="contact-dialog-header flex flex-col items-start gap-2 self-stretch">
                    <DialogTitle className="contact-dialog-title self-stretch text-2xl font-semibold leading-8 not-italic tracking-[-0.01em] text-[#18181b] [font-family:var(--font-poppins),sans-serif]">
                        {t('title')}
                    </DialogTitle>
                    <DialogDescription className="contact-dialog-description self-stretch text-base font-normal leading-6 not-italic text-[#71717a] [font-family:var(--font-instrument-sans),var(--font-inter),-apple-system,BlinkMacSystemFont,sans-serif]">
                        {t('description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="contact-dialog-form flex flex-col items-start gap-6 self-stretch">
                    <div className="contact-dialog-field flex flex-col items-start gap-2.5 self-stretch">
                        <label className="contact-dialog-label text-sm font-medium leading-5 not-italic text-[#27272a] [font-family:var(--font-instrument-sans),var(--font-inter),-apple-system,BlinkMacSystemFont,sans-serif]">
                            {t('name')} <span className="contact-dialog-required ml-1 text-[#f87171]">*</span>
                        </label>
                        <Input 
                            placeholder={t('namePlaceholder')}
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="contact-dialog-input flex h-12 items-center self-stretch rounded-[12px] border border-[#d4d4d8] bg-background px-4 py-0 text-[15px] font-normal leading-normal text-[#18181b] transition-all duration-200 ease-[ease] placeholder:text-[#71717a] [&:hover]:border-[#a1a1aa] [&:hover]:bg-[#fafafa] focus:border-[#71717a] focus:bg-background focus:outline-none focus:shadow-[0_0_0_4px_rgb(24_24_27/0.08)] focus-visible:border-[#71717a] focus-visible:ring-0 md:text-[15px] md:leading-(--text-sm--line-height) [font-family:var(--font-instrument-sans),var(--font-inter),-apple-system,BlinkMacSystemFont,sans-serif]"
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="contact-dialog-field flex flex-col items-start gap-2.5 self-stretch">
                        <label className="contact-dialog-label text-sm font-medium leading-5 not-italic text-[#27272a] [font-family:var(--font-instrument-sans),var(--font-inter),-apple-system,BlinkMacSystemFont,sans-serif]">
                            {t('email')} <span className="contact-dialog-required ml-1 text-[#f87171]">*</span>
                        </label>
                        <Input 
                            type="email"
                            placeholder={t('emailPlaceholder')}
                            value={formData.email}
                            onChange={(e) => {
                                const value = e.target.value;
                                setFormData(prev => ({ ...prev, email: value }));
                                if (!value.trim()) {
                                    setEmailError(t('emailRequired'));
                                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
                                    setEmailError(t('invalidEmail'));
                                } else {
                                    setEmailError('');
                                }
                            }}
                            className="contact-dialog-input flex h-12 items-center self-stretch rounded-[12px] border border-[#d4d4d8] bg-background px-4 py-0 text-[15px] font-normal leading-normal text-[#18181b] transition-all duration-200 ease-[ease] placeholder:text-[#71717a] [&:hover]:border-[#a1a1aa] [&:hover]:bg-[#fafafa] focus:border-[#71717a] focus:bg-background focus:outline-none focus:shadow-[0_0_0_4px_rgb(24_24_27/0.08)] focus-visible:border-[#71717a] focus-visible:ring-0 md:text-[15px] md:leading-(--text-sm--line-height) [font-family:var(--font-instrument-sans),var(--font-inter),-apple-system,BlinkMacSystemFont,sans-serif]"
                            disabled={isSubmitting}
                        />
                        {emailError && (
                            <p className="contact-dialog-error mt-1 text-xs leading-4 text-[#f97373]">
                                {emailError}
                            </p>
                        )}
                    </div>

                    <div className="contact-dialog-field flex flex-col items-start gap-2.5 self-stretch">
                        <label className="contact-dialog-label text-sm font-medium leading-5 not-italic text-[#27272a] [font-family:var(--font-instrument-sans),var(--font-inter),-apple-system,BlinkMacSystemFont,sans-serif]">
                            {t('message')} <span className="contact-dialog-required ml-1 text-[#f87171]">*</span>
                        </label>
                        <Textarea 
                            placeholder={t('messagePlaceholder')}
                            value={formData.message}
                            onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                            className="contact-dialog-textarea flex min-h-[140px] resize-none self-stretch rounded-[12px] border border-[#d4d4d8] bg-background p-4 text-[15px] font-normal leading-[1.5] text-[#18181b] transition-all duration-200 ease-[ease] placeholder:text-[#71717a] [&:hover]:border-[#a1a1aa] [&:hover]:bg-[#fafafa] focus:border-[#71717a] focus:bg-background focus:outline-none focus:shadow-[0_0_0_4px_rgb(24_24_27/0.08)] focus-visible:border-[#71717a] focus-visible:ring-0 md:text-[15px] [font-family:var(--font-instrument-sans),var(--font-inter),-apple-system,BlinkMacSystemFont,sans-serif]"
                            disabled={isSubmitting}
                        />
                    </div>
                </div>

                <div className="contact-dialog-button-area flex items-center justify-end gap-4 self-stretch pt-2">
                    <Button
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        data-landing-button-id={
                            trackingSurface === 'landing_v2' ? 'business.contact_modal.cancel' : undefined
                        }
                        data-landing-button-screen={trackingSurface === 'landing_v2' ? 'business' : undefined}
                        data-landing-action={trackingSurface === 'landing_v2' ? 'toggle' : undefined}
                        className={cn(
                            "contact-dialog-cancel-button flex h-11 items-center justify-center rounded-[10px] border border-transparent bg-transparent px-6 py-0 text-[15px] font-medium leading-(--text-sm--line-height) text-[#52525b] transition-all duration-200 ease-[ease] [&:hover:not(:disabled)]:bg-[#f4f4f5] [&:hover:not(:disabled)]:text-[#18181b]",
                            isFormValid && "active"
                        )}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!isFormValid || isSubmitting}
                        data-landing-button-id={
                            trackingSurface === 'landing_v2' ? 'business.contact_modal.submit' : undefined
                        }
                        data-landing-button-screen={trackingSurface === 'landing_v2' ? 'business' : undefined}
                        data-landing-action={trackingSurface === 'landing_v2' ? 'submit' : undefined}
                        className={cn(
                            "contact-dialog-submit-button flex h-11 items-center justify-center rounded-[10px] border-0 bg-[#18181b] px-8 py-0 text-[15px] font-semibold leading-(--text-sm--line-height) text-white opacity-50 transition-all duration-200 ease-[ease] [&.active:not(:disabled)]:opacity-100 [&.active]:shadow-[0_8px_20px_rgb(24_24_27/0.18)] [&:hover:not(:disabled)]:bg-[#27272a] [&:hover:not(:disabled)]:-translate-y-px [&:hover:not(:disabled)]:shadow-[0_6px_18px_rgb(24_24_27/0.22)] [&:active:not(:disabled)]:translate-y-0 disabled:bg-[#e4e4e7] disabled:text-[#a1a1aa] disabled:opacity-30",
                            isFormValid && "active"
                        )}
                    >
                        {isSubmitting ? t('sending') : t('sendMessage')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
